/**
 * A chat: the header (title, project chip, the agents and drafts toggles),
 * the messages, and the composer.
 *
 * What the page shows is the chat as fetched with the reply in flight laid
 * over it (`withTurn`). The stream itself lives in the turn store rather than
 * here, so leaving mid-reply does not cut it off and coming back finds it
 * still going; when a turn settles, the page folds it into its own list so
 * the next turn can replace it.
 *
 * A reply that ends with a question docks it above the composer, and every
 * send while it waits is its answer (`answers`), typed or picked.
 *
 * Errors show inline under the reply they belong to, each with the one thing
 * that helps: when a limit resets and where usage lives, the model menu when a
 * model is gone, Retry otherwise. `ai_disabled` needs nothing here — the turn
 * store re-reads `/me` and the gate swaps the whole app for the off screen.
 *
 * A temporary chat says so in the header, beside "Save chat", and offers no
 * Branch. Saved, it joins the sidebar and is kept when left.
 *
 * The agents its replies started are in the side panel, beside its drafts:
 * the artifacts its replies made (DesignPanel), and the older drafts kept in
 * a message (ArtifactPanel). A reply that makes or changes an artifact opens
 * it. In design mode (the header's palette) the panel goes first and an
 * artifact is open whenever the chat has one.
 * An agent's own address opens its parent with the agent in the panel
 * (`?agent=`).
 *
 * A PC's Dispatch thread (`chat.deviceId`) is this page too, at
 * `/dispatch/:deviceId`, with the PC in the header in place of a title. Its
 * messages carry time labels, a reply waiting on the PC says why, and a
 * message the Worker holds for the PC is drawn after it. The hub's `dispatch`
 * frames are how it learns the thread changed, and it reads the chat again.
 * Stop ends a reply waiting on the PC from here, where no stream is open.
 * Its calls are the PC's to take on, through the runner (deviceRunner.ts),
 * never this page's unless it is that PC. In the Android app, a phone paired
 * with the PC opens its own sheet for what the PC asks (Review), and one not
 * paired is offered pairing in the PC's menu; the page draws neither sheet.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_EFFORT } from '@wire';
import { nativeApprove, nativePair } from '../android';
import {
  ApiError,
  branchChat,
  deleteChat,
  getChat,
  listArtifacts,
  patchChat,
  setLeaf,
  stopReply,
  type ArtifactSummary,
  type Attachment,
  type Chat,
  type ChatPatch,
  type Effort,
  type Message,
  type ToolRun
} from '../api';
import { agentsLabel } from '../components/agentsText';
import { AgentsPanel } from '../components/AgentsPanel';
import { ArtifactPanel, PanelTabs, type PanelTab } from '../components/ArtifactPanel';
import { OpenArtifact } from '../components/ArtifactTile';
import { DesignMenu } from '../components/DesignControls';
import { DesignPanel } from '../components/DesignPanel';
import { DevicePicker } from '../components/DevicePicker';
import { heldShown, pairable, reviewable, waitsOnPc } from '../components/dispatchText';
import { Composer } from '../components/composer/Composer';
import { shortName } from '../components/composer/ModelMenu';
import { resetWhen } from '../components/composer/usageText';
import { MessageList } from '../components/MessageList';
import { answeredText, answerItems, pendingAsk } from '../components/pendingAsk';
import { MainHeader, useShell } from '../components/Shell';
import { MonoLabel, PROJECT_COLOURS, Ph } from '../components/ui';
import { ds } from '../ds';
import { dropChat, peekChat, stashChat, toSummary } from '../hooks/chatStash';
import { markSaved, markTemporary } from '../hooks/temporaryChats';
import { useSelectAll } from '../hooks/useSelectAll';
import { usePairings } from '../hooks/usePairings';
import { useStickToBottom } from '../hooks/useStickToBottom';
import { takePendingVoice } from '../hooks/voiceStart';
import { VoiceMode } from '../components/voice/VoiceMode';
import { unlockAudio } from '../audio/speaker';
import {
  attachTurn,
  clearHeld,
  forgetTurn,
  peekTurn,
  queueTurn,
  rejoinTurn,
  setResearch,
  startTurn,
  stopTurn,
  takeOn,
  takeQueued,
  useHeld,
  useLiveTurn,
  useResearch,
  withTurn,
  type QueuedTurn,
  type TurnFailure
} from '../hooks/useTurns';
import { setFront } from '../hub';
import { PANEL_OVERLAY } from '../hooks/useViewport';
import { Link, useRouter } from '../router';
import { useAgents, useApp } from '../state';

function Notice({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        fontSize: 13.5,
        lineHeight: 1.5,
        color: 'var(--text-muted)'
      }}
    >
      <Ph name="warning-circle" size={15} color="var(--danger-500)" />
      {children}
    </div>
  );
}

const inlineAction: CSSProperties = { fontSize: 13.5, color: 'var(--text-link)' };

function FailureLine({
  failure,
  modelName,
  continued,
  onRetry,
  onPickModel
}: {
  failure: TurnFailure;
  modelName: string;
  /** A refused take-on of a waiting reply: nothing else takes it on again. */
  continued: boolean;
  onRetry: () => void;
  onPickModel: () => void;
}) {
  const retry = (
    <button type="button" className="bare" onClick={onRetry} style={inlineAction}>
      Retry
    </button>
  );

  if (failure.code === 'limit_reached') {
    const which =
      failure.window === 'weekly'
        ? 'your weekly limit'
        : failure.window === 'fiveHour'
          ? 'your 5-hour limit'
          : failure.window === 'month'
            ? 'your limit for this month'
            : 'your limit';
    const when = resetWhen(failure.resetsAt);
    return (
      <Notice>
        <span>
          {failure.window === 'organisation'
            ? 'Your organisation has reached its Assist limit for this month.'
            : `You have reached ${which}.`}
          {when ? ` It resets ${when}.` : ''}
        </span>
        <Link to="/settings/usage" style={inlineAction}>
          See usage
        </Link>
        {continued ? retry : null}
      </Notice>
    );
  }

  if (failure.code === 'no_images') {
    return (
      <Notice>
        <span>{failure.message}</span>
        <button type="button" className="bare" onClick={onPickModel} style={inlineAction}>
          Choose another model
        </button>
        {retry}
      </Notice>
    );
  }

  if (failure.code === 'model_unavailable') {
    return (
      <Notice>
        <span>{modelName} is not available.</span>
        <button type="button" className="bare" onClick={onPickModel} style={inlineAction}>
          Choose another model
        </button>
        {continued ? retry : null}
      </Notice>
    );
  }

  if (failure.code === 'ai_disabled') {
    return (
      <Notice>
        <span>{failure.message}</span>
        {continued ? retry : null}
      </Notice>
    );
  }

  return (
    <Notice>
      <span>{failure.message}</span>
      {retry}
    </Notice>
  );
}

export function ChatPage({ id }: { id: string }) {
  const { catalogue, chats, projects, devices, thread, upsertChat, touchChat, patchMe, refreshMe, refreshLists, removeChat, setAgents } = useApp();
  const { navigate, query } = useRouter();
  const { narrow, width, panelHost } = useShell();
  const { Button } = ds();

  const [chat, setChat] = useState<Chat | null>(() => peekChat(id));
  const [loadFailed, setLoadFailed] = useState<'missing' | 'failed' | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [panelId, setPanelId] = useState<string | null>(null);
  /** The artifact open in the panel, in place of a message's draft: at
   *  first the one the address names (`?artifact=`, from the library). */
  const [designId, setDesignId] = useState<string | null>(() => query.get('artifact'));
  const [artifactList, setArtifactList] = useState<ArtifactSummary[]>([]);
  const [panel, setPanel] = useState<PanelTab | null>(() => (query.get('agent') ? 'agents' : query.get('artifact') ? 'drafts' : null));
  /** The agent open in the panel, or null for the list. */
  const [watching, setWatching] = useState<string | null>(() => query.get('agent'));
  const agents = useAgents(id);
  const [modelMenuRequest, setModelMenuRequest] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  /** The part of a reply the next message answers, picked with Quote. */
  const [quote, setQuote] = useState<string | null>(null);
  /** Voice mode's audio, while it is open; started by the click that opened
   *  it, here or on Home. */
  const [voice, setVoice] = useState<AudioContext | null>(() => takePendingVoice(id));
  const live = useLiveTurn(id);
  const research = useResearch(id);
  const { scrollRef, contentRef, content, stick } = useStickToBottom();
  useSelectAll(content);

  const turnApp = useMemo(
    () => ({ upsertChat, touchChat, patchMe, refreshMe }),
    [upsertChat, touchChat, patchMe, refreshMe]
  );

  // A chat handed over by Home or a branch is drawn at once; anything else is
  // fetched.
  useEffect(() => {
    dropChat(id);
    if (chat) return;
    let cancelled = false;
    setLoadFailed(null);
    getChat(id)
      .then((c) => {
        if (cancelled) return;
        if (c.agent) navigate(`/chat/${c.agent.parentChatId}?agent=${c.id}`, { replace: true });
        else if (c.deviceId) navigate(`/dispatch/${c.deviceId}`, { replace: true });
        else setChat(c);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'ai_disabled') void refreshMe();
        setLoadFailed(err instanceof ApiError && err.status === 404 ? 'missing' : 'failed');
      });
    return () => {
      cancelled = true;
    };
    // `chat` is deliberately left out: this runs per chat id (and per Retry),
    // not every time the chat changes.
  }, [id, attempt]);

  // The agents as the chat was fetched; the hub keeps them since.
  const fetchedAgents = chat?.agents;
  useEffect(() => {
    if (fetchedAgents) setAgents(id, fetchedAgents);
  }, [fetchedAgents]);

  // Opened after a reload, a temporary chat is one again: kept out of the
  // sidebar, and deleted once left.
  const temporary = !!chat?.temporary;
  useEffect(() => {
    if (temporary) markTemporary(id);
  }, [id, temporary]);

  // Fold a settled turn in, so the next one can take its place in the store.
  // Then what was queued while it streamed goes, unless the reply waits.
  useEffect(() => {
    if (!live || live.phase === 'streaming') return;
    // Settled here, so no reply runs as far as this page knows. A PC's thread
    // goes on in the same reply, which is joined again when it next runs.
    if (pcId) joined.current = null;
    setChat((c) =>
      c
        ? {
            ...c,
            live: null,
            messages: withTurn(c.messages, live, true),
            title: live.title ?? c.title,
            context: live.context ?? c.context
          }
        : c
    );
    const body = live.final?.body;
    const waits = body?.kind === 'text' && (!!body.pending?.length || !!body.stepLimit);
    if (live.phase === 'done' && !waits && chat) {
      const q = takeQueued(id);
      if (q) sendQueued(q);
    }
  }, [live]);

  // A model that has gone: open the menu so the person can pick another.
  const failedSeq = live?.failure?.code === 'model_unavailable' ? live.seq : null;
  useEffect(() => {
    if (failedSeq !== null) setModelMenuRequest((n) => n + 1);
  }, [failedSeq]);

  const messages = useMemo(() => (chat ? withTurn(chat.messages, live) : []), [chat, live]);
  const question = useMemo(() => pendingAsk(messages), [messages]);
  const streaming = live?.phase === 'streaming';

  // A PC's thread: the PC, as far as the list knows it, and what the Worker
  // holds for it. Both gone when the PC is forgotten, here or elsewhere.
  const pcId = chat?.deviceId ?? null;
  const pc = pcId ? (devices?.find((d) => d.id === pcId) ?? null) : null;
  const waitingOnPc = !!pcId && waitsOnPc(messages[messages.length - 1]);
  const heldNow = useHeld(id);
  const held = pcId ? heldShown(heldNow, messages) : [];
  useEffect(() => {
    // Not for a list that holds no PC at all, which may be one that did not load.
    if (pcId && devices?.length && !pc) navigate('/dispatch', { replace: true });
  }, [pcId, devices, pc]);
  // On a phone that can pair: what it may answer for the PC, and pairing it.
  const pairings = usePairings(!!pcId);
  const asked = messages[messages.length - 1];
  const review = pcId && asked && reviewable(asked, pcId, pairings) ? () => void nativeApprove(pcId, id, asked.id) : undefined;
  const pair = pairable(pcId, pairings) ? () => void nativePair() : undefined;

  // The chat as it is now, for a PC's thread whose reply waits, was taken on
  // or ended out of sight. One read at a time; one more if a frame came
  // meanwhile. A settled turn gives way to what was read: its copy of the
  // reply is older, and has no `deviceWait`.
  const reading = useRef<'idle' | 'busy' | 'again'>('idle');
  const reread = () => {
    if (reading.current !== 'idle') {
      reading.current = 'again';
      return;
    }
    reading.current = 'busy';
    getChat(id)
      .then((next) => {
        const turn = peekTurn(id);
        if (turn && (turn.phase === 'done' || turn.phase === 'stopped')) forgetTurn(id);
        setChat(next);
        if (!waitsOnPc(next.messages[next.messages.length - 1])) clearHeld(id);
      })
      .catch(() => undefined)
      .finally(() => {
        const more = reading.current === 'again';
        reading.current = 'idle';
        if (more) reread();
      });
  };
  useEffect(() => {
    if (pcId && thread.seq && (thread.chatId === id || thread.chatId === null)) reread();
  }, [thread.seq]);
  // A message was just held: the thread may not have known its reply waits.
  const heldCount = heldNow.length;
  useEffect(() => {
    if (pcId && heldCount) reread();
  }, [heldCount]);
  // Once a reply streams the message went in with it.
  useEffect(() => {
    if (streaming) clearHeld(id);
  }, [streaming]);
  // The sidebar's list first: a rename lands there, and a turn's title does
  // too, at the same moment it reaches the turn.
  const listed = chats.find((c) => c.id === id);
  const title = listed?.title || live?.title || chat?.title || '';

  // The hub hears which chat is in front, for as long as it is.
  useEffect(() => {
    setFront(id);
    return () => setFront(null);
  }, [id]);

  // A reply running when the chat was opened, or begun since on another
  // device (the list hears of it from the hub): joined, once for each reply.
  const joined = useRef<string | null>(null);
  const liveNow = listed?.live ?? chat?.live ?? null;
  const running = liveNow ? `${id}:${liveNow.messageId ?? ''}` : null;
  useEffect(() => {
    if (!chat || !running || joined.current === running || live?.phase === 'streaming') return;
    joined.current = running;
    attachTurn({ chat: toSummary({ ...chat, title }), app: turnApp });
  }, [running]);

  // Back on the page after a reply was cut off: it is joined again.
  useEffect(() => {
    if (!chat) return;
    const back = () => {
      if (document.visibilityState === 'visible') rejoinTurn({ chat: toSummary({ ...chat, title }), app: turnApp });
    };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [chat, title, turnApp]);

  // A reply that ended out of sight: the chat as it is now, and what was
  // queued behind it goes.
  const endedSeq = live?.failure?.code === 'ended' ? live.seq : null;
  useEffect(() => {
    if (endedSeq === null) return;
    getChat(id).then(
      (next) => {
        forgetTurn(id);
        setChat(next);
        upsertChat(toSummary(next));
        const q = takeQueued(id);
        if (q) sendQueued(q);
      },
      () => setNotice('Assist did not answer. Try again.')
    );
  }, [endedSeq]);

  // A reply left waiting on code to run goes on, and inside the desktop app
  // so does one waiting on the machine.
  const lastStored = chat?.messages[chat.messages.length - 1];
  const waitingFor =
    lastStored?.body.kind === 'text' ? (lastStored.body.pending ?? []).map((c) => c.id).join(',') : '';
  // Refused, it goes on again from Retry, not by itself.
  const continueRefused = live?.kind === 'continue' && !!live.failure?.refused;
  // Not while a reply runs in the chat: it is being taken on already, and
  // the list's copy of it still reads as waiting until the reply is written.
  useEffect(() => {
    if (!chat || !lastStored || !waitingFor || streaming || continueRefused || running) return;
    // A reply waiting on the PC is taken on when the hub wakes it, by the
    // runner, which says it is running first.
    if (pcId && waitsOnPc(lastStored)) return;
    takeOn({ chat: toSummary({ ...chat, title }), message: lastStored, app: turnApp });
    // Once per set of calls, and again only if a stream or a run was in the way.
  }, [waitingFor, streaming, running]);
  const context = live?.context ?? chat?.context ?? null;
  const project = chat?.projectId ? projects.find((p) => p.id === chat.projectId) ?? null : null;
  const colours = project ? PROJECT_COLOURS[project.colour] : undefined;

  const artifacts = messages.filter((m) => m.body.kind === 'artifact');
  const panelMessage = panel === 'drafts' && panelId && !designId ? artifacts.find((m) => m.id === panelId) ?? null : null;
  const agentsShown = panel === 'agents' && agents.length > 0;
  const working = agents.filter((a) => a.agent?.state === 'working').length;

  // The artifacts its replies made: as listed, read again once a reply
  // settles, and as the replies' rows name them meanwhile.
  const settledAt = live && live.phase !== 'streaming' ? live.seq : 0;
  const loaded = !!chat;
  useEffect(() => {
    if (!loaded || pcId) return;
    let gone = false;
    listArtifacts(id).then(
      (r) => !gone && setArtifactList(r.artifacts),
      () => undefined
    );
    return () => {
      gone = true;
    };
  }, [id, loaded, settledAt]);
  const made = useMemo(() => {
    const out: Array<NonNullable<ToolRun['artifact']>> = [];
    for (const m of messages) if (m.body.kind === 'text') for (const c of m.body.calls ?? []) if (c.artifact) out.push(c.artifact);
    if (live?.phase === 'streaming') for (const c of live.calls) if (c?.artifact) out.push(c.artifact);
    return out;
  }, [messages, live]);
  const knownVersion = (artifactId: string) =>
    Math.max(artifactList.find((a) => a.id === artifactId)?.version ?? 0, ...made.filter((a) => a.id === artifactId).map((a) => a.version));
  const newest = made[made.length - 1]?.id ?? artifactList[artifactList.length - 1]?.id ?? null;
  const designShown = panel === 'drafts' && !!designId;
  const design = !!chat?.design && !pcId;

  const openDraft = (messageId: string) => {
    setDesignId(null);
    setPanelId(messageId);
    setPanel('drafts');
  };
  const openDesign = (artifactId: string) => {
    setDesignId(artifactId);
    setPanel('drafts');
  };
  const openNewest = () => (newest ? openDesign(newest) : artifacts.length ? openDraft(artifacts[artifacts.length - 1]!.id) : undefined);
  // A reply that makes or changes an artifact opens it.
  const liveMade = live?.phase === 'streaming' ? live.calls.filter((c) => c?.artifact).at(-1)?.artifact : undefined;
  const liveKey = liveMade ? `${liveMade.id}:${liveMade.version}` : null;
  useEffect(() => {
    if (liveMade) openDesign(liveMade.id);
  }, [liveKey]);
  // In design mode an artifact is open whenever there is one.
  useEffect(() => {
    if (design && !panel && newest) openDesign(newest);
  }, [design, newest]);

  const changeDesign = (patch: Pick<ChatPatch, 'design' | 'designSystemId'>) => {
    if (!chat) return;
    if (patch.design !== undefined) setChat({ ...chat, design: patch.design });
    patchChat(id, patch)
      .then(() => getChat(id))
      .then(
        (next) => setChat((c) => (c ? { ...c, design: next.design, designSystemId: next.designSystemId, designSystem: next.designSystem } : c)),
        () => setNotice('That did not go through. Try again.')
      );
  };
  const openAgent = (agentId: string) => {
    setWatching(agentId);
    setPanel('agents');
  };
  const hasDrafts = artifacts.length > 0 || !!newest;
  const tabs =
    hasDrafts && agents.length ? (
      <PanelTabs
        value={panel === 'agents' ? 'agents' : 'drafts'}
        onChange={(tab) => (tab === 'agents' ? setPanel('agents') : designId ? openDesign(designId) : panelMessage ? openDraft(panelMessage.id) : openNewest())}
      />
    ) : undefined;

  /** "Change this…": a message asking for a change to the artifact. */
  const askChange = (text: string, artifact: { id: string; title: string }) => {
    if (!chat || streaming) return;
    setNotice(null);
    stick();
    startTurn({ chat: toSummary({ ...chat, title }), kind: 'turn', input: { text, attachments: [], artifact }, app: turnApp });
  };

  const send = (text: string, attachments: Attachment[], answers = question?.messageId) => {
    // Throwing keeps the composer's draft; there is nothing to send into yet.
    if (!chat) throw new Error('The chat has not loaded.');
    // Sent while a reply streams, it waits for the reply.
    if (streaming) {
      queueTurn(id, { text, attachments, ...(quote ? { quote } : {}) });
      setQuote(null);
      return;
    }
    setNotice(null);
    stick();
    startTurn({
      chat: toSummary({ ...chat, title }),
      kind: 'turn',
      input: { text, attachments, ...(answers ? { answers } : {}), ...(quote ? { quote } : {}) },
      app: turnApp
    });
    setQuote(null);
  };

  const sendQueued = (q: QueuedTurn) => {
    if (!chat) return;
    setNotice(null);
    stick();
    const answers = question?.messageId;
    startTurn({
      chat: toSummary({ ...chat, title }),
      kind: 'turn',
      input: { text: q.text, attachments: q.attachments, ...(answers ? { answers } : {}), ...(q.quote ? { quote: q.quote } : {}) },
      app: turnApp
    });
  };

  /** Stop the reply. In a PC's thread one waiting on the PC has no stream
   *  here to stop: the Worker ends it, and the PC lets go. */
  const stop = () => {
    if (pcId && !streaming) void stopReply(id).catch(() => setNotice('Assist did not answer. Try again.'));
    else stopTurn(id);
  };

  /** Stop the reply where it is, and send what was queued. */
  const sendNow = () => {
    const stopped = stopTurn(id, true);
    if (stopped) setChat((c) => (c ? { ...c, messages: withTurn(c.messages, stopped, true) } : c));
    const q = takeQueued(id);
    if (q) sendQueued(q);
  };

  const answerQuestion = (picks: string[]) => {
    if (!chat || !question) throw new Error('The chat has not loaded.');
    const answered = answerItems(question.ask, picks);
    setNotice(null);
    stick();
    startTurn({
      chat: toSummary({ ...chat, title }),
      kind: 'turn',
      input: { text: answeredText(answered), attachments: [], answers: question.messageId, picks, answered },
      app: turnApp
    });
  };

  const edit = (messageId: string, text: string) => {
    if (!chat) return;
    const original = messages.find((m) => m.id === messageId);
    const attachments = original?.body.kind === 'text' ? (original.body.attachments ?? []) : [];
    // The new version keeps the old one's quote, as the Worker does.
    const kept = original?.body.kind === 'text' ? original.body.quote : undefined;
    setNotice(null);
    stick();
    startTurn({
      chat: toSummary({ ...chat, title }),
      kind: 'turn',
      input: { text, attachments, edits: messageId, ...(kept ? { quote: kept } : {}) },
      app: turnApp
    });
  };

  const switchVersion = async (messageId: string) => {
    setNotice(null);
    try {
      const next = await setLeaf(id, { messageId });
      forgetTurn(id);
      setChat(next);
    } catch (err) {
      setNotice(err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer. Try again.');
    }
  };

  /** Allow or deny what a waiting reply wants to do; it goes on either way.
   *  At its step limit, allowing goes on and denying ends it there. */
  const decide = (message: Message, decision: 'allowed' | 'allowed_chat' | 'denied') => {
    if (!chat || message.body.kind !== 'text') return;
    const approvals = (message.body.pending ?? [])
      .filter((c) => c.needs === 'approval')
      .map((c) => ({ callId: c.id, decision }));
    setNotice(null);
    stick();
    startTurn({
      chat: toSummary({ ...chat, title }),
      kind: 'continue',
      waiting: message,
      resume: message.body.stepLimit ? (decision === 'denied' ? { stop: true } : {}) : { approvals },
      app: turnApp
    });
  };

  const regenerate = (messageId: string) => {
    if (!chat) return;
    setNotice(null);
    stick();
    startTurn({ chat: toSummary({ ...chat, title }), kind: 'retry', replacing: messageId, app: turnApp });
  };

  /**
   * Retry after a failed turn. Refused outright, nothing was stored, so the
   * message is sent again. Failed once the reply had started, that reply is
   * regenerated — the Worker stores it under the id `start` gave.
   */
  const retryFailed = () => {
    if (!chat || !live) return;
    if (live.kind === 'retry' && live.replacing) {
      regenerate(live.failure?.refused ? live.replacing : live.messageId ?? live.replacing);
    } else if (live.messageId && !live.failure?.refused) {
      regenerate(live.messageId);
    } else if (live.input) {
      // A refused answer is still the last message, so no question is
      // pending: it goes again as the answer it was, the dock's included.
      setNotice(null);
      stick();
      startTurn({ chat: toSummary({ ...chat, title }), kind: 'turn', input: live.input, app: turnApp });
    } else if (live.kind === 'continue' && live.failure?.refused) {
      // Refused, the reply still waits and its block is back; inside the
      // desktop app it is taken on again. One that is not waiting any more
      // went on elsewhere, so the chat is fetched as it is now.
      setNotice(null);
      if (live.failure.code === 'invalid_field') {
        getChat(id).then(
          (next) => {
            forgetTurn(id);
            setChat(next);
          },
          () => setNotice('Assist did not answer. Try again.')
        );
      } else {
        forgetTurn(id);
        if (live.waiting) takeOn({ chat: toSummary({ ...chat, title }), message: live.waiting, app: turnApp, again: true });
      }
    }
  };

  const branch = async (messageId: string) => {
    setNotice(null);
    try {
      const next = await branchChat(id, { messageId });
      stashChat(next);
      upsertChat(toSummary(next));
      navigate(`/chat/${next.id}`);
    } catch (err) {
      setNotice(err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer. Try again.');
    }
  };

  const [saving, setSaving] = useState(false);
  const save = async () => {
    setNotice(null);
    setSaving(true);
    try {
      const summary = await patchChat(id, { temporary: false });
      // Unmarked first: the list ignores a chat still marked.
      markSaved(id);
      upsertChat(summary);
      setChat((c) => (c ? { ...c, temporary: false } : c));
    } catch (err) {
      setNotice(err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const changeModel = (model: string) => {
    if (!chat) return;
    const before = chat.model;
    setChat({ ...chat, model });
    patchChat(id, { model }).catch(() => setChat((c) => (c ? { ...c, model: before } : c)));
  };

  // The slider picks at every stop it passes. One PATCH at a time, and only
  // the latest pick goes next, so the row ends on the last stop.
  const effortWanted = useRef<Effort | null>(null);
  const effortSaving = useRef<Promise<void>>(Promise.resolve());

  const changeEffort = (effort: Effort) => {
    if (!chat) return;
    const before = chat.effort;
    setChat({ ...chat, effort });
    effortWanted.current = effort;
    effortSaving.current = effortSaving.current.then(async () => {
      if (effortWanted.current !== effort) return;
      try {
        await patchChat(id, { effort });
      } catch {
        if (effortWanted.current === effort) setChat((c) => (c ? { ...c, effort: before } : c));
      }
    });
  };

  const onCardChange = (message: Message) => {
    setChat((c) => (c ? { ...c, messages: c.messages.map((m) => (m.id === message.id ? message : m)) } : c));
    // An answered approval clears the sidebar's "waiting for you" dot.
    if (message.body.kind === 'approval') void refreshLists();
  };

  // On a phone the composer lines up with the messages, clear of the home bar.
  const composerPad = narrow ? '8px 16px max(16px, env(safe-area-inset-bottom))' : '8px 24px 20px';

  return (
    <>
      <MainHeader>
        {pcId ? (
          <DevicePicker current={pc} name={title} {...(pair ? { onPair: pair } : {})} />
        ) : (
          <span
            title={title || undefined}
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: 'var(--text-strong)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0
            }}
          >
            {chat ? title || 'New chat' : ''}
          </span>
        )}
        {project && narrow ? (
          // On a phone the project is its colour alone, and the title has the room.
          <Link
            to={`/project/${project.id}`}
            label={project.name || 'Untitled project'}
            className="tap"
            style={{
              width: 10,
              height: 10,
              flex: 'none',
              borderRadius: 3,
              background: colours?.dot ?? 'var(--primary)'
            }}
          />
        ) : project ? (
          <Link
            to={`/project/${project.id}`}
            style={{
              flex: 'none',
              maxWidth: 200,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 8px',
              borderRadius: 6,
              background: colours?.soft ?? 'var(--primary-soft)',
              color: colours?.dot ?? 'var(--primary)',
              fontSize: 11.5
            }}
          >
            <Ph name="folder-simple" size={12} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</span>
          </Link>
        ) : null}
        {temporary ? <MonoLabel style={{ flex: 'none' }}>Temporary</MonoLabel> : null}
        <span style={{ flex: 1 }} />
        {temporary ? (
          <Button variant="ghost" density="app" size="sm" disabled={saving} onClick={() => void save()}>
            Save chat
          </Button>
        ) : null}
        {agents.length ? (
          <button
            type="button"
            className="bare hov-tint"
            aria-label={agentsLabel(working)}
            title="Agents"
            aria-pressed={agentsShown}
            onClick={() => setPanel(agentsShown ? null : 'agents')}
            style={{
              minWidth: 32,
              height: 30,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              padding: '0 6px',
              borderRadius: 'var(--radius-chrome)',
              color: agentsShown ? 'var(--primary)' : 'var(--text-muted)'
            }}
          >
            <Ph name="users-three" size={17} className={working ? 'pulse' : undefined} />
            {working ? <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{working}</span> : null}
          </button>
        ) : null}
        {chat && !pcId ? (
          <DesignMenu
            design={design}
            designSystemId={chat.designSystemId}
            inherited={chat.designSystem?.inherited ? chat.designSystem.name : null}
            inProject={!!chat.projectId}
            onDesign={(on) => changeDesign({ design: on })}
            onSystem={(designSystemId) => changeDesign({ designSystemId })}
          />
        ) : null}
        {hasDrafts ? (
          <button
            type="button"
            className="bare hov-tint"
            aria-label="Drafts"
            title="Drafts"
            aria-pressed={!!panelMessage || designShown}
            onClick={() => (panelMessage || designShown ? setPanel(null) : openNewest())}
            style={{
              width: 32,
              height: 30,
              flex: 'none',
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--radius-chrome)',
              color: panelMessage || designShown ? 'var(--primary)' : 'var(--text-muted)'
            }}
          >
            <Ph name="sidebar-simple" size={17} style={{ transform: 'scaleX(-1)' }} />
          </button>
        ) : null}
      </MainHeader>

      <div ref={scrollRef} className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div
          ref={contentRef}
          style={{
            maxWidth: 760,
            margin: '0 auto',
            padding: narrow ? '12px 16px 24px' : '12px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }}
        >
          {chat ? (
            <OpenArtifact.Provider value={openDesign}>
              <MessageList
                chatId={id}
                messages={messages}
                live={live?.phase === 'streaming' ? live : null}
                busy={streaming}
                onRetry={regenerate}
                {...(temporary || pcId ? {} : { onBranch: (messageId: string) => void branch(messageId) })}
                {...(pcId ? { dispatch: { device: pc, held, ...(review ? { review } : {}) } } : {})}
                onEdit={edit}
                onDecide={decide}
                onSwitch={(messageId) => void switchVersion(messageId)}
                onChange={onCardChange}
                onOpenArtifact={(m) => openDraft(m.id)}
                onQuote={setQuote}
                onOpenAgent={openAgent}
              />
            </OpenArtifact.Provider>
          ) : loadFailed === 'missing' ? (
            <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' }}>
              There is no chat here. <Link to="/" style={{ color: 'var(--text-link)' }}>Start a new one</Link>
            </div>
          ) : loadFailed === 'failed' ? (
            <div style={{ padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
              <Notice>
                <span>This chat did not load.</span>
                <button type="button" className="bare" onClick={() => setAttempt((n) => n + 1)} style={inlineAction}>
                  Retry
                </button>
              </Notice>
            </div>
          ) : null}

          {chat && live?.phase === 'failed' && live.failure && live.failure.code !== 'ended' ? (
            <FailureLine
              failure={live.failure}
              modelName={shortName(chat.model, catalogue?.models)}
              continued={continueRefused}
              onRetry={retryFailed}
              onPickModel={() => setModelMenuRequest((n) => n + 1)}
            />
          ) : null}
          {notice ? <Notice>{notice}</Notice> : null}
        </div>
      </div>

      <div style={{ flex: 'none', padding: composerPad }}>
        <Composer
          variant="chat"
          chatId={id}
          model={chat?.model ?? ''}
          onModelChange={changeModel}
          effort={chat?.effort ?? DEFAULT_EFFORT}
          onEffortChange={changeEffort}
          context={context}
          suggestion={live?.phase === 'done' ? live.suggestion : null}
          question={question}
          busy={streaming || waitingOnPc}
          onStop={stop}
          onSendNow={sendNow}
          onSend={send}
          quote={quote}
          onQuoteClear={() => setQuote(null)}
          onQuoteRestore={setQuote}
          research={research}
          onResearch={(on) => setResearch(id, on)}
          onAnswer={answerQuestion}
          modelMenuRequest={modelMenuRequest}
          temporary={temporary}
          onVoice={() => setVoice(unlockAudio())}
          {...(pcId ? { placeholder: `Message ${pc?.name ?? title}`, pcName: pc?.name ?? title } : {})}
        />
      </div>

      {voice && chat ? (
        <VoiceMode
          chat={toSummary({ ...chat, title })}
          app={turnApp}
          context={voice}
          onClose={(spoke) => {
            void voice.close().catch(() => undefined);
            setVoice(null);
            // Opened from Home and closed before a word: the chat it made
            // is nothing, and goes.
            if (!spoke && !chat.messages.length) {
              removeChat(id);
              void deleteChat(id).catch(() => undefined);
              navigate('/');
              return;
            }
            // A turn stopped mid-sentence can drop its title on the way out.
            void refreshLists();
          }}
        />
      ) : null}

      {panelMessage && panelHost
        ? createPortal(
            <ArtifactPanel
              message={panelMessage}
              tabs={tabs}
              onClose={() => setPanel(null)}
              overlay={width < PANEL_OVERLAY}
              narrow={narrow}
            />,
            panelHost
          )
        : null}
      {designShown && designId && panelHost
        ? createPortal(
            <DesignPanel
              artifactId={designId}
              known={knownVersion(designId)}
              chatId={id}
              others={artifactList}
              onPick={openDesign}
              tabs={tabs}
              first={design}
              busy={streaming}
              onChange={askChange}
              onClose={() => setPanel(null)}
              overlay={width < PANEL_OVERLAY}
              narrow={narrow}
            />,
            panelHost
          )
        : null}
      {agentsShown && panelHost
        ? createPortal(
            <AgentsPanel
              agents={agents}
              watching={watching}
              onWatch={setWatching}
              tabs={tabs}
              onClose={() => setPanel(null)}
              overlay={width < PANEL_OVERLAY}
              narrow={narrow}
            />,
            panelHost
          )
        : null}
    </>
  );
}
